import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { format, parseISO } from "date-fns";
import { PLTransaction, TransactionLineItemDetail } from "@/interfaces/profitAndLoss.interface";
import { fetchProfitAndLossLineItems, fetchTransactionLineItems } from "@/services/profitLoss.service";
import { buildMonthMatrix, formatCurrency } from "./utils";

export interface DrilldownLine {
  name: string;
  hasChildren: boolean;
}

interface LineItemDrilldownSheetProps {
  line: DrilldownLine | null;
  columns: string[];
  range: { startDate: string; endDate: string };
  onClose: () => void;
}

interface SelectedCell {
  rowLabel: string;
  monthIndex: number;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "—";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
};

const isDrillableTxn = (txn: PLTransaction): boolean =>
  !!txn.txnId && !!txn.organizationId && (txn.type === "Invoice" || txn.type === "Bill");

const LineItemDetailTable = ({ detail }: { detail: TransactionLineItemDetail }) => (
  <div className="rounded-lg border border-border/40 bg-background overflow-hidden">
    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border/30 flex items-center justify-between">
      <span>
        {detail.docNumber ? `#${detail.docNumber}` : ""} {detail.counterpartyName ?? ""} &middot; {formatDate(detail.date)}
      </span>
      <span className="font-semibold text-foreground/80">{formatCurrency(detail.total)}</span>
    </div>
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="border-b border-border/20 bg-muted/20">
          <th className="text-left py-1.5 px-3 font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
          <th className="text-left py-1.5 px-3 font-semibold uppercase tracking-wider text-muted-foreground">Item</th>
          <th className="text-right py-1.5 px-3 font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
          <th className="text-right py-1.5 px-3 font-semibold uppercase tracking-wider text-muted-foreground">Unit Price</th>
          <th className="text-right py-1.5 px-3 font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
        </tr>
      </thead>
      <tbody>
        {detail.lines.map((line, i) => (
          <tr key={i} className="border-b border-border/10 last:border-0">
            <td className="py-1.5 px-3 text-foreground/80">{line.description || "—"}</td>
            <td className="py-1.5 px-3 text-muted-foreground">{line.itemName ?? "—"}</td>
            <td className="py-1.5 px-3 text-right tabular-nums">{line.quantity ?? "—"}</td>
            <td className="py-1.5 px-3 text-right tabular-nums">
              {line.unitPrice != null ? formatCurrency(line.unitPrice) : "—"}
            </td>
            <td className="py-1.5 px-3 text-right tabular-nums font-medium">{formatCurrency(line.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TransactionTable = ({ transactions }: { transactions: PLTransaction[] }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [detail, setDetail] = useState<TransactionLineItemDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const cacheRef = useRef(new Map<string, TransactionLineItemDetail>());

  const handleRowClick = (txn: PLTransaction, idx: number) => {
    if (!isDrillableTxn(txn)) return;

    if (expandedIdx === idx) {
      setExpandedIdx(null);
      return;
    }

    setExpandedIdx(idx);
    setDetailError(false);

    const cached = cacheRef.current.get(txn.txnId!);
    if (cached) {
      setDetail(cached);
      return;
    }

    setDetail(null);
    setDetailLoading(true);
    fetchTransactionLineItems(txn.organizationId!, txn.txnId!, txn.type as "Invoice" | "Bill")
      .then((data) => {
        cacheRef.current.set(txn.txnId!, data);
        setDetail(data);
      })
      .catch(() => setDetailError(true))
      .finally(() => setDetailLoading(false));
  };

  return (
    <div className="rounded-lg border border-border/40 bg-card overflow-hidden">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border/40 bg-muted/30">
            <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
            <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
            <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
            <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn, i) => {
            const drillable = isDrillableTxn(txn);
            return (
              <Fragment key={i}>
                <tr
                  className={`border-b border-border/20 last:border-0 hover:bg-muted/10 transition-colors ${
                    drillable ? "cursor-pointer" : ""
                  }`}
                  onClick={() => handleRowClick(txn, i)}
                  title={drillable ? "Click to see line items" : undefined}
                >
                  <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(txn.date)}</td>
                  <td className="py-2 px-3 max-w-[220px]">
                    <div className="truncate text-xs text-foreground/80" title={txn.description}>
                      {txn.description || "—"}
                    </div>
                    <div className="truncate text-xs text-muted-foreground/70">{txn.entityName}</div>
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-muted/50 text-muted-foreground border border-border/30 w-fit">
                      {txn.type || "—"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold tabular-nums text-foreground/90 whitespace-nowrap">
                    {formatCurrency(txn.amount)}
                  </td>
                </tr>
                {expandedIdx === i && (
                  <tr>
                    <td colSpan={4} className="p-3 bg-muted/10">
                      {detailLoading && (
                        <div className="flex items-center gap-2 py-3 justify-center text-muted-foreground">
                          <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs">Loading line items...</span>
                        </div>
                      )}
                      {!detailLoading && detailError && (
                        <p className="text-xs text-muted-foreground italic py-1">Failed to load line items.</p>
                      )}
                      {!detailLoading && !detailError && detail && <LineItemDetailTable detail={detail} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const LineItemDrilldownSheet = ({ line, columns, range, onClose }: LineItemDrilldownSheetProps) => {
  const [transactions, setTransactions] = useState<PLTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  useEffect(() => {
    if (!line) {
      setTransactions([]);
      setSelectedCell(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setTransactions([]);
    setSelectedCell(null);
    fetchProfitAndLossLineItems(range, line.name)
      .then((data) => {
        if (!cancelled) setTransactions(data);
      })
      .catch(() => {
        if (!cancelled) setTransactions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line?.name, range.startDate, range.endDate]);

  const groupBy = line?.hasChildren ? "subLine" : "vendor";
  const matrix = useMemo(() => buildMonthMatrix(transactions, columns, groupBy), [transactions, columns, groupBy]);

  if (!line) return null;

  const total = transactions.reduce((s, t) => s + t.amount, 0);

  return (
    <Sheet open={!!line} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-none md:w-[90vw] overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="text-xl leading-tight">{line.name}</SheetTitle>
          <p className="text-sm text-muted-foreground">
            {formatDate(range.startDate)} – {formatDate(range.endDate)} &middot; Grouped by{" "}
            {groupBy === "subLine" ? "sub-account" : "vendor"}
          </p>
          <p className="text-xs text-muted-foreground/70">
            Invoice and bill rows below can be expanded further to see their individual line items.
          </p>
        </SheetHeader>

        <div className="py-5 space-y-6">
          <div className="bg-muted/30 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Total</span>
            <span className="text-2xl font-bold tabular-nums text-foreground">{formatCurrency(total)}</span>
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Loading transactions...</span>
            </div>
          )}

          {!loading && transactions.length === 0 && (
            <p className="text-sm text-muted-foreground italic py-2">No transactions found for this line in the selected period.</p>
          )}

          {!loading && matrix.rows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border/40">
              <table className="min-w-max w-full text-sm border-collapse">
                <thead className="sticky top-0 z-20 bg-background">
                  <tr className="border-b border-border/40 bg-background">
                    <th className="sticky left-0 z-30 bg-background text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {groupBy === "subLine" ? "Sub-account" : "Vendor / Payee"}
                    </th>
                    {matrix.months.map((month) => (
                      <th
                        key={month}
                        className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap"
                      >
                        {month}
                      </th>
                    ))}
                    <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => (
                    <Fragment key={row.label}>
                      <tr className="border-b border-border/20 hover:bg-muted/10 transition-colors">
                        <td className="sticky left-0 z-10 bg-card py-2 px-3 text-sm font-medium text-foreground truncate max-w-[220px]">
                          {row.label}
                        </td>
                        {row.cells.map((cell, i) => {
                          const isSelected = selectedCell?.rowLabel === row.label && selectedCell.monthIndex === i;
                          const hasData = cell.transactions.length > 0;
                          return (
                            <td
                              key={matrix.months[i]}
                              className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${
                                hasData ? "cursor-pointer hover:bg-muted/40 text-foreground/90" : "text-muted-foreground/40"
                              } ${isSelected ? "bg-muted/50 font-semibold" : ""}`}
                              onClick={() =>
                                hasData &&
                                setSelectedCell(isSelected ? null : { rowLabel: row.label, monthIndex: i })
                              }
                            >
                              {formatCurrency(cell.total)}
                            </td>
                          );
                        })}
                        <td className="py-2 px-3 text-right font-semibold tabular-nums text-foreground whitespace-nowrap">
                          {formatCurrency(row.rowTotal)}
                        </td>
                      </tr>
                      {selectedCell?.rowLabel === row.label && (
                        <tr>
                          <td colSpan={matrix.months.length + 2} className="p-3 bg-muted/10">
                            <TransactionTable transactions={row.cells[selectedCell.monthIndex]?.transactions ?? []} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LineItemDrilldownSheet;
